import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { FaHome, FaSignOutAlt, FaCheck, FaBars, FaTimes, FaChevronDown, FaChevronRight } from "react-icons/fa";
import SummaryMetrics from "./SummaryMetrics";
import ProductDetails from "./ProductDetails";
import { StatusBadge, STATUS_OPTIONS } from "./constants";
import "./AdminDashboard.css";
import ActionMenu from "./ActionMenu";
import UserManagement from "./UserManagement";
import BulkActions from "./BulkActions";
import { NotificationProvider } from "./NotificationContext";
import NotificationBell from "./NotificationBell";
import TrackerTable from "./TrackerTable";
import PricingRules, { defaultRules } from "./PricingRules";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { io } from "socket.io-client";
import Pagination from "./Pagination";
import NetSuiteItemsUpload from "./NetSuiteItemsUpload";

const ADMIN = "Admin";

// Shared by every path that turns a raw /api response into a product
// object this component works with (the paginated list fetch, and the
// single-submission-by-id fetch used for direct URL/bookmark/reload
// access) - previously each had its own separate, partial copy of this,
// and the single-fetch one was missing several fields (history, syncLog,
// date, ...) that other code assumes are always present in a particular
// shape (an array, a Date object, etc.), causing crashes ("history is not
// iterable", "Cannot read properties of undefined (reading
// 'toDateString')") only for products opened that way. One function used
// everywhere means the two paths can't drift apart like that again.
function normalizeProduct(d) {
  return {
    ...d,
    id: d.submission_id,
    depositor: d.depositor_name_contributor || "",
    status: d.status || "Pending",
    syncStatus: d.syncStatus || "not-synced",
    date: d.submitted_at ? new Date(d.submitted_at) : new Date(),
    lastUpdated: new Date(),
    history: d.history || [],
    syncLog: d.syncLog || [],
    is_read: Number(d.is_read) === 1 ? 1 : 0,
    applications: typeof d.applications === "string" ? d.applications :
            Array.isArray(d.applications) ? d.applications.join(", ") : "",
  };
}

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProductId = searchParams.get("product");

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("latestSubmitted");
  const [statusFilter, setStatusFilter] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [note, setNote] = useState("");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [showPricing, setShowPricing] = useState(false);
  const [pricingRules, setPricingRules] = useState(defaultRules);
  const [trackerData, setTrackerData] = useState([]);
  const [showNetSuiteUpload, setShowNetSuiteUpload] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Debounced search — wait 400ms after typing stops before fetching
  // so we don't hammer the API on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Batch grouping (expand/collapse) ──────────────────────────────
  const [expandedBatches, setExpandedBatches] = useState({});

  // ── Mobile hamburger menu ─────────────────────────────────────────
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const statusFilterRef = useRef(null);
  const socket = useMemo(() => io("https://dshb-deposits.its.uiowa.edu"), []);

  // ── Fetch — search is now sent to the server so it searches ALL records,
  //    not just the current page. The API needs to support ?search=...
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      console.log(`📥 Fetching page ${currentPage}, size ${pageSize}, search "${debouncedSearch}"`);

      const statusParam = statusFilter.length > 0 ? statusFilter.join(",") : "";
      const searchParam = debouncedSearch ? encodeURIComponent(debouncedSearch) : "";

      const res = await fetch(
        `https://dshb-deposits.its.uiowa.edu/api/admin/products?page=${currentPage}&limit=${pageSize}&status=${statusParam}&search=${searchParam}`
      );
      const data = await res.json();
      console.log("🟢 API Response:", data);

      const formatted = (data.data || []).map(normalizeProduct);

      setProducts(formatted);
      setTotalCount(data.pagination?.total || 0);
      setTotalPages(data.pagination?.totalPages || 1);

      console.log(`✅ Fetched ${formatted.length} products (page ${currentPage}/${data.pagination?.totalPages})`);
    } catch (err) {
      console.error("❌ Failed to fetch products:", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, debouncedSearch]);

  const fetchTracker = useCallback(async () => {
    try {
      const res = await fetch(`https://dshb-deposits.its.uiowa.edu/api/tracker?page=1&limit=500`);
      const data = await res.json();

      const safe = Array.isArray(data.data) ? data.data : data?.data;
      setTrackerData(Array.isArray(safe) ? safe : []);
    } catch (err) {
      console.error("❌ Tracker fetch failed:", err);
      setTrackerData([]);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sort, statusFilter]);

  useEffect(() => {
    fetchTracker();
  }, [fetchTracker]);

  // ── Load saved pricing rules once on mount. Falls back to the
  // hardcoded defaultRules (already the initial state) if this fails or
  // nothing's been saved to the server yet.
  useEffect(() => {
    fetch("https://dshb-deposits.its.uiowa.edu/api/pricing-rules")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.rules) && data.rules.length > 0) {
          setPricingRules(data.rules);
        }
      })
      .catch((err) => console.error("❌ Failed to load pricing rules:", err));
  }, []);

  // ── Keep the open product in sync with the URL — so a reload (or a
  // shared/bookmarked link) lands back on the same product page instead of
  // the list. Tries the already-loaded page first; falls back to fetching
  // the product directly by id (e.g. right after a reload, before this
  // page's product list has necessarily loaded, or if it's on another page).
  useEffect(() => {
    if (!urlProductId) {
      setSelectedProduct(null);
      return;
    }
    if (selectedProduct && String(selectedProduct.submission_id || selectedProduct.id) === String(urlProductId)) {
      return;
    }
    const fromList = products.find(
      (p) => String(p.submission_id || p.id) === String(urlProductId)
    );
    if (fromList) {
      setSelectedProduct(fromList);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`https://dshb-deposits.its.uiowa.edu/api/submission/${urlProductId}`);
        if (!res.ok) throw new Error("Not found");
        const data = await res.json();
        setSelectedProduct(normalizeProduct(data));
      } catch (err) {
        console.error("Failed to load product from URL:", err);
        setSearchParams({});
      }
    })();
  }, [urlProductId, products]); // eslint-disable-line

  useEffect(() => {
    socket.emit("join-room", { role: "admin", userId: user?.user_id });

    socket.on("new-deposit-notification", () => {
      console.log("📢 New deposit received, refreshing...");
      fetchProducts();
    });

    return () => socket.off("new-deposit-notification");
  }, [socket, user, fetchProducts]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // UPDATED: was setPricingRules(updatedRules) only -- pure React state,
  // no API call at all, which is exactly why edits (e.g. "Allow Profit")
  // reverted to the hardcoded defaults on every reload. Now persists to
  // the server (backend/index.js's /api/pricing-rules, saved to a JSON
  // file) so it survives reloads and restarts.
  const handleRulesChange = (updatedRules) => {
    setPricingRules(updatedRules);
    fetch("https://dshb-deposits.its.uiowa.edu/api/pricing-rules", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rules: updatedRules }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) console.error("❌ Failed to save pricing rules:", data.error);
      })
      .catch((err) => console.error("❌ Failed to save pricing rules:", err));
  };

  const saveDetails = (updatedProduct) => {
    // `original` can legitimately be undefined - a product opened via
    // direct URL/bookmark/reload (see the single-submission fetch above)
    // might not be in the currently-loaded `products` list at all yet.
    // Same reasoning applies to updatedProduct.history below: it isn't
    // guaranteed to be an array from every caller, so this always falls
    // back to [] rather than assuming the shape and throwing.
    const original = products.find((p) => p.id === updatedProduct.id);
    let changes = [];

    Object.keys(updatedProduct).forEach((key) => {
      if (JSON.stringify(original?.[key]) !== JSON.stringify(updatedProduct[key])) {
        changes.push(`${key} changed from "${original?.[key]}" to "${updatedProduct[key]}"`);
      }
    });

    const finalProduct = {
      ...updatedProduct,
      lastUpdated: new Date(),
      history: [
        ...(Array.isArray(updatedProduct.history) ? updatedProduct.history : []),
        {
          action: "Admin Updated Fields",
          by: ADMIN,
          date: new Date().toLocaleString(),
          status: updatedProduct.status,
          note,
          changes
        }
      ]
    };

    setProducts((prev) =>
      prev.map((p) => (p.id === finalProduct.id ? finalProduct : p))
    );

    setSelectedProduct(finalProduct);
    setNote("");
  };

  // ── displayProducts — sorting only (search & status now done server-side) ──
  const displayProducts = useMemo(() => {
    let data = [...products];

    // Sort client-side (the returned page is already filtered by server)
    if (sort === "latestSubmitted") {
      data.sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (sort === "oldestSubmitted") {
      data.sort((a, b) => new Date(a.date) - new Date(b.date));
    } else if (sort === "latestUpdated") {
      data.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
    } else if (sort === "oldestUpdated") {
      data.sort((a, b) => new Date(a.lastUpdated) - new Date(b.lastUpdated));
    }
    // else if (sort === "nameAZ") {
    //   data.sort((a, b) => String(a.item_name_number || "").localeCompare(String(b.item_name_number || "")));
    // } else if (sort === "nameZA") {
    //   data.sort((a, b) => String(b.item_name_number || "").localeCompare(String(a.item_name_number || "")));
    // }

    return data;
  }, [products, sort]);

  // ── Group displayProducts into batches ────────────────────────────
  const batches = useMemo(() => {
    const groups = {};
    displayProducts.forEach((p) => {
      const dt = p.date instanceof Date ? p.date : new Date(p.date);
      const batchKey = [
        p.depositor_id ?? p.depositorId ?? "x",
        dt.getFullYear(), dt.getMonth(), dt.getDate(), dt.getHours(), dt.getMinutes(),
      ].join("-");

      if (!groups[batchKey]) {
        groups[batchKey] = {
          batchId: batchKey,
          date: dt,
          depositor: p.depositor_name_contributor || p.depositor || "",
          products: [],
        };
      }
      groups[batchKey].products.push(p);
    });

    return Object.values(groups).sort((a, b) => b.date - a.date);
  }, [displayProducts]);

  const toggleBatch = (batchId) =>
    setExpandedBatches((prev) => ({ ...prev, [batchId]: !prev[batchId] }));

  const statusPillColor = (status) => {
    const map = {
      Pending: { bg: "#fef9c3", color: "#854d0e" },
      Approved: { bg: "#dcfce7", color: "#15803d" },
      Reviewed: { bg: "#dbeafe", color: "#1e40af" },
      "Correction Requested": { bg: "#fee2e2", color: "#991b1b" },
    };
    return map[status] || { bg: "#f3f4f6", color: "#374151" };
  };

  const openProduct = async (p) => {
    setSelectedProduct(p);
    setSearchParams({ product: p.submission_id || p.id });
    try {
      await fetch("https://dshb-deposits.its.uiowa.edu/api/admin/mark-read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: p.id })
      });
      setProducts((prev) =>
        prev.map((item) => (item.id === p.id ? { ...item, is_read: 1 } : item))
      );
    } catch (err) {
      console.error("Mark read failed:", err);
    }
  };

  const toggleRow = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((r) => r !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedRows.length === displayProducts.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(displayProducts.map((p) => p.id));
    }
  };

  const metrics = {
    total: totalCount,
    pending: products.filter((p) => p.status === "Pending").length,
    correction: products.filter((p) => p.status === "Correction Requested").length,
    reviewed: products.filter((p) => p.status === "Reviewed").length,
    approved: products.filter((p) => p.status === "Approved").length,
    today: products.filter((p) => p.date.toDateString() === new Date().toDateString()).length
  };

  // ── Nav items ─────────────────────────────────────────────────────
  const navItems = (
    <>
      <div className="nav-primary">
        <div
          className={`nav-link ${activeTab === "home" ? "active" : ""}`}
          onClick={() => { setActiveTab("home"); setMobileMenuOpen(false); }}
        >
          <FaHome /> <span>Home</span>
        </div>

        <div
          className={`nav-link ${activeTab === "tracker" ? "active" : ""}`}
          onClick={() => { setActiveTab("tracker"); setMobileMenuOpen(false); }}
        >
          <FaCheck /> <span>Tracker</span>
        </div>
      </div>

      <div className="nav-account">
        <div className="nav-notif-wrap">
          <NotificationBell />
        </div>

        <ActionMenu
          onUserManagement={() => { setShowUserManagement(true); setMobileMenuOpen(false); }}
          onPricing={() => { setShowPricing(true); setMobileMenuOpen(false); }}
          onSettings={() => alert("Settings page")}
          onNetSuiteItems={() => { setShowNetSuiteUpload(true); setMobileMenuOpen(false); }}
        />

        <button className="nav-logout-btn" onClick={handleLogout}>
          <FaSignOutAlt /> <span>Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <NotificationProvider>
      <div className="admin-dashboard">
        {/* NAVBAR */}
        <div className="dashboard-navbar">
          <button
            className="hamburger-btn"
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <div className="navbar-left">
            <div className="navbar-logo">
              <img src="/dshb-logo.png" alt="DSHB Logo" className="logo-img" />
            </div>
          </div>

          <div className="navbar-right navbar-right--desktop">
            {navItems}
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-nav-menu">
            {navItems}
          </div>
        )}

        {showUserManagement && <UserManagement onClose={() => setShowUserManagement(false)} />}
        {showNetSuiteUpload && <NetSuiteItemsUpload onClose={() => setShowNetSuiteUpload(false)} />}

        <div className="main-section">
          {showPricing && (
            <div className="pricing-panel-wrapper">
              <PricingRules
                initialRules={pricingRules}
                onClose={() => setShowPricing(false)}
                onRulesChange={handleRulesChange}
              />
            </div>
          )}

          {activeTab === "tracker" ? (
            <TrackerTable
              trackerData={trackerData}
              setTrackerData={setTrackerData}
              products={products}
              setSelectedProduct={setSelectedProduct}
              setActiveTab={setActiveTab}
            />
          ) : !selectedProduct ? (
            <>
              <SummaryMetrics metrics={metrics} />

              {/* TOOLBAR */}
              <div className="toolbar">
                <div className="toolbar-left">
                  <input
                    className={`search-input ${selectedRows.length > 0 ? "disabled" : ""}`}
                    placeholder="Search by ID, Item Name, or Depositor..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    disabled={selectedRows.length > 0}
                  />
                  <select
                    className={`toolbar-select small-select ${selectedRows.length > 0 ? "disabled" : ""}`}
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    disabled={selectedRows.length > 0}
                  >
                    <option value="latestSubmitted">Latest Submitted</option>
                    <option value="oldestSubmitted">Oldest Submitted</option>
                    <option value="latestUpdated">Latest Updated</option>
                    <option value="oldestUpdated">Oldest Updated</option>
                    {/* <option value="nameAZ">Name (A-Z)</option> */}
                    {/* <option value="nameZA">Name (Z-A)</option> */}
                  </select>

                  <div className={`status-filter ${selectedRows.length > 0 ? "disabled" : ""}`} ref={statusFilterRef}>
                    <div className="toolbar-select status-trigger"
                      onClick={() => {if (selectedRows.length === 0) setShowStatusDropdown(!showStatusDropdown);}}>
                        Filter Status ▾
                    </div>
                    {showStatusDropdown && (
                      <div className="status-dropdown">
                        {STATUS_OPTIONS.map((status) => (
                          <div
                            key={status}
                            className={`status-option ${statusFilter.includes(status) ? "active" : ""}`}
                            onClick={() => {
                              if (statusFilter.includes(status))
                                setStatusFilter(statusFilter.filter((s) => s !== status));
                              else setStatusFilter([...statusFilter, status]);
                            }}
                          >
                            {status}
                          </div>
                        ))}
                        <div className="status-actions">
                          <span className="status-icon" title="Select All"
                            onClick={() => setStatusFilter([...STATUS_OPTIONS])}>
                            ✔️
                          </span>
                          <span className="status-icon" title="Reset"
                            onClick={() => setStatusFilter([])}>
                            🔄
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="toolbar-right" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{ padding: "3px 6px", fontSize: "12px", height: "28px" }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>

                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </div>

              {/* SELECT ALL bar */}
              <div style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 15px", marginBottom: "10px",
                background: "#fff", borderRadius: "8px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              }}>
                <input
                  type="checkbox"
                  checked={
                    displayProducts.length > 0 &&
                    selectedRows.length === displayProducts.length
                  }
                  onChange={toggleSelectAll}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ fontSize: "12px", color: "#374151", fontWeight: 500 }}>
                  {selectedRows.length > 0
                    ? `${selectedRows.length} selected`
                    : `Select all (${displayProducts.length})`}
                </span>
              </div>

              <BulkActions
                selectedProducts={selectedRows}
                allProducts={products}
                setProducts={setProducts}
                clearSelection={() => setSelectedRows([])}
                pricingRules={pricingRules}
              />

              {loading && <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>Loading...</div>}

              {!loading && (
                <div className="main-table-wrapper" style={{ padding: "12px", maxHeight: "700px" }}>
                  {batches.length === 0 ? (
                    <div style={{ padding: "40px 20px", textAlign: "center", color: "#9ca3af" }}>
                      No submissions found.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {batches.map((batch) => {
                        const isExpanded = expandedBatches[batch.batchId];
                        const statusCounts = batch.products.reduce((acc, p) => {
                          const s = p.status || "Pending";
                          acc[s] = (acc[s] || 0) + 1;
                          return acc;
                        }, {});
                        const hasUnread = batch.products.some((p) => p.is_read === 0);
                        const allSelected = batch.products.every((p) => selectedRows.includes(p.id));

                        return (
                          <div key={batch.batchId} style={{
                            border: "1px solid #e2e8f0", borderRadius: "8px",
                            overflow: "hidden", background: hasUnread ? "#eff6ff" : "#fff",
                          }}>
                            {/* Batch header */}
                            <div
                              onClick={() => toggleBatch(batch.batchId)}
                              style={{
                                padding: "12px 16px", cursor: "pointer",
                                display: "flex", alignItems: "center", gap: "12px",
                                userSelect: "none",
                                background: isExpanded ? "#f8fafc" : "transparent",
                                borderBottom: isExpanded ? "1px solid #e2e8f0" : "none",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => {
                                  const ids = batch.products.map((p) => p.id);
                                  if (allSelected) {
                                    setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)));
                                  } else {
                                    setSelectedRows((prev) => [...new Set([...prev, ...ids])]);
                                  }
                                }}
                              />
                              <span style={{ color: "#6b7280", fontSize: "13px", flexShrink: 0 }}>
                                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                  <span style={{ fontWeight: 600, fontSize: "13px", color: "#1a2c5b" }}>
                                    {batch.depositor || "Unknown depositor"}
                                  </span>
                                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                                    {batch.date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                    {" · "}
                                    {batch.date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                                  </span>
                                  <span style={{
                                    background: "#e0e7ff", color: "#3730a3",
                                    borderRadius: "20px", padding: "2px 10px",
                                    fontSize: "11px", fontWeight: 600,
                                  }}>
                                    {batch.products.length} antibod{batch.products.length !== 1 ? "ies" : "y"}
                                  </span>
                                  {Object.entries(statusCounts).map(([s, count]) => {
                                    const sc = statusPillColor(s);
                                    return (
                                      <span key={s} style={{
                                        background: sc.bg, color: sc.color,
                                        borderRadius: "20px", padding: "2px 10px",
                                        fontSize: "11px", fontWeight: 600,
                                      }}>
                                        {s}: {count}
                                      </span>
                                    );
                                  })}
                                  {hasUnread && (
                                    <span style={{
                                      background: "#dc2626", color: "#fff",
                                      borderRadius: "20px", padding: "2px 10px",
                                      fontSize: "10px", fontWeight: 700,
                                    }}>NEW</span>
                                  )}
                                </div>
                                {!isExpanded && (
                                  <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#9ca3af",
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {batch.products.slice(0, 3).map((p) => p.item_name_number || "—").join(", ")}
                                    {batch.products.length > 3 ? ` and ${batch.products.length - 3} more…` : ""}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Expanded antibody table */}
                            {isExpanded && (
                              <div style={{ overflowX: "auto" }}>
                                <table className="product-table" style={{ marginTop: 0 }}>
                                  <thead>
                                    <tr>
                                      <th></th>
                                      <th>ID</th>
                                      <th>Item Name</th>
                                      <th>Isotype</th>
                                      <th>Status</th>
                                      <th>Sync</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {batch.products.map((p) => (
                                      <tr
                                        key={p.id}
                                        onClick={() => openProduct(p)}
                                        className={`clickable-row ${p.is_read === 0 ? "new-row" : ""}`}
                                      >
                                        <td>
                                          <input
                                            type="checkbox"
                                            checked={selectedRows.includes(p.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => toggleRow(p.id)}
                                          />
                                        </td>
                                        <td>{p.id}</td>
                                        <td>{p.item_name_number || "-"}</td>
                                        <td>{p.isotype || "-"}</td>
                                        <td><StatusBadge status={p.status} /></td>
                                        <td>
                                          {p.syncStatus === "synced" && "✔️"}
                                          {p.syncStatus === "not-synced" && "—"}
                                          {p.syncStatus === "sync-failed" && "❌"}
                                          {p.syncStatus === "pending" && "⏳"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <ProductDetails
              product={selectedProduct}
              status={selectedProduct?.status}
              pricingRules={pricingRules}
              saveDetails={saveDetails}
              setSelectedProduct={setSelectedProduct}
              onBack={() => { setSelectedProduct(null); setSearchParams({}); }}
              products={products}
              setProducts={setProducts}
              setEditMode={setEditMode}
              editMode={editMode}
              note={note}
              setNote={setNote}
              setShowEmailModal={() => {}}
              trackerData={trackerData.filter(
                t => String(t.submission_id) === String(selectedProduct.submission_id || selectedProduct.id)
              )}
              setTrackerData={setTrackerData}
            />
          )}
        </div>
      </div>
    </NotificationProvider>
  );
}